import { useEffect, useMemo, useRef, useState } from "react";

import { Chess, type Square } from "chess.js";

import {
  demoBoardMoves,
  lichessBoardPalette,
  type AccentTone,
} from "@/shared/config/siteContent";
import { replayUciMove, resolveUciMove } from "@/shared/ui/chessMove";
import { ChessSurface } from "@/shared/ui/ChessSurface";

type BoardFrameProps = {
  title: string;
  status: string;
  accent: AccentTone;
  eyebrow?: string;
  moves?: string[];
  interactive?: boolean;
};

export function BoardFrame({
  title,
  status,
  accent,
  eyebrow = "Live board",
  moves = demoBoardMoves,
  interactive = false,
}: BoardFrameProps) {
  const [boardWidth, setBoardWidth] = useState(0);
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [game, setGame] = useState(() => {
    const next = new Chess();
    for (const move of moves) {
      replayUciMove(next, move);
    }
    return next;
  });
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);

  useEffect(() => {
    const node = boardWrapRef.current;
    if (!node) {
      return;
    }

    const updateWidth = () => {
      const isCompact = window.matchMedia("(max-width: 768px)").matches;
      const boardChromeWidth = isCompact ? 30 : 0;
      const next = Math.floor(Math.min(Math.max(node.clientWidth - boardChromeWidth, 220), 360));
      setBoardWidth(next > 0 ? next : 360);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const next = new Chess();
    for (const move of moves) {
      replayUciMove(next, move);
    }
    setGame(next);
    setSelectedSquare(null);
    setLegalTargets([]);
  }, [moves]);

  const lastMove = useMemo(() => {
    const history = game.history({ verbose: true });
    return history.at(-1) ?? null;
  }, [game]);

  const feedItems = useMemo(() => {
    const history = game.history({ verbose: true }) as Array<{
      color: "w" | "b";
      san: string;
      from: Square;
      to: Square;
    }>;
    return history
      .map((entry, index) => ({
        ...entry,
        label:
          entry.color === "w"
            ? `${Math.floor(index / 2) + 1}. ${entry.san}`
            : `... ${entry.san}`,
      }))
      .slice(-5);
  }, [game]);

  const commitMove = (from: Square, to: Square) => {
    const next = new Chess(game.fen());
    const moveUci = resolveUciMove(next, from, to);
    if (!moveUci) {
      return false;
    }
    replayUciMove(next, moveUci);
    setGame(next);
    setSelectedSquare(null);
    setLegalTargets([]);
    return true;
  };

  const selectSquare = (square: Square) => {
    const movesForSquare = game.moves({ square, verbose: true });
    if (movesForSquare.length === 0) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }

    setSelectedSquare(square);
    setLegalTargets(movesForSquare.map((move) => move.to));
  };

  const onSquareClick = (squareName: Square) => {
    if (!interactive) {
      return;
    }

    if (selectedSquare) {
      if (squareName === selectedSquare) {
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }

      if (commitMove(selectedSquare, squareName)) {
        return;
      }
    }

    const piece = game.get(squareName);
    if (!piece || piece.color !== game.turn()) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }

    selectSquare(squareName);
  };

  const onPieceDrop = (sourceSquare: Square, targetSquare?: Square) => {
    if (!interactive || !targetSquare) {
      return false;
    }
    return commitMove(sourceSquare, targetSquare);
  };

  const onPieceDragBegin = (_piece: string, sourceSquare: Square) => {
    if (!interactive) {
      return;
    }
    selectSquare(sourceSquare);
  };

  const customSquareStyles = useMemo(() => {
    const styles: Record<string, { backgroundColor?: string; boxShadow?: string; background?: string }> = {};

    if (selectedSquare) {
      styles[selectedSquare] = {
        boxShadow: lichessBoardPalette.selected,
      };
    }

    for (const square of legalTargets) {
      styles[square] = {
        background: lichessBoardPalette.legalTarget,
      };
    }

    if (lastMove) {
      styles[lastMove.from] = {
        ...(styles[lastMove.from] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
      styles[lastMove.to] = {
        ...(styles[lastMove.to] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
    }

    return styles;
  }, [lastMove, legalTargets, selectedSquare]);

  return (
    <section
      className={`board-frame board-frame--${accent}`}
      aria-label={title}
    >
      <div className="board-frame__top">
        <div>
          {eyebrow ? <p className="board-frame__eyebrow">{eyebrow}</p> : null}
          <h2>{title}</h2>
        </div>
        <span className="board-frame__status">{status}</span>
      </div>

      <div className="board-frame__body">
        <div className="board-frame__real-board" ref={boardWrapRef}>
          {boardWidth > 0 ? (
            <ChessSurface
              id={`frame-board-${accent}`}
              boardWidth={boardWidth}
              position={game.fen()}
              arePiecesDraggable={interactive && !game.isGameOver()}
              onPieceDrop={onPieceDrop}
              onPieceDragBegin={onPieceDragBegin}
              onSquareClick={onSquareClick}
              customSquareStyles={customSquareStyles}
              darkSquareColor={lichessBoardPalette.dark}
              lightSquareColor={lichessBoardPalette.light}
            />
          ) : null}
        </div>

        <aside className="board-feed" aria-label="Move feed">
          {feedItems.map((entry, index) => (
            <article key={`${entry.from}-${entry.to}-${index}`} className="board-feed__item">
              <div className="board-feed__line">
                <strong>{entry.label}</strong>
                <span>{entry.color === "w" ? "Human" : "Model"}</span>
              </div>
              <p>{entry.from} to {entry.to}</p>
            </article>
          ))}
        </aside>
      </div>
    </section>
  );
}
